import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { ReactComponent as SvgMap } from "../../assets/img/map.svg";
import {
  GraphMapPropsType,
  MapValueType,
  ShutdownTypeType,
} from "./GraphMapTypes";

const two_colors_model = ["#BCBBBC", "#E11429"];
const two_colors_borders = ["#EBEBEB"];

const GraphMap: React.FC<GraphMapPropsType> = ({
  vals,
  shutdown_types,
  regions_list,
  ...rest
}) => {
  const mapObject = useRef<SVGSVGElement>(null);

  // Add null/undefined checks for props using useMemo to prevent re-creation on every render
  const safeVals = useMemo(() => vals || [], [vals]);
  const safeShutdownTypes = useMemo(
    () => shutdown_types || [],
    [shutdown_types],
  );
  const safeRegionsList = useMemo(() => regions_list || [], [regions_list]);

  useEffect(() => {
    const states = Array.from(
      mapObject.current?.getElementsByClassName("state") ?? [],
    ) as HTMLElement[];

    states.forEach((state) => {
      state.style.cssText =
        "fill: " +
        two_colors_model[0] +
        ";stroke: " +
        two_colors_borders[0] +
        ";stroke-opacity:1;";
    });
  }, [mapObject]);

  const set_colors_from_sd_type = useCallback(
    (sd_type: ShutdownTypeType[], vals: MapValueType[]) => {
      const get_region_by_to_map_connection = (to_map_connection: string) => {
        let region = "";
        for (let tmc_1 in safeRegionsList) {
          if (safeRegionsList[tmc_1].to_map_connection === to_map_connection) {
            region = safeRegionsList[tmc_1].name;
          }
        }
        return region;
      };
      const root = mapObject.current;
      const states = Array.from(
        root?.getElementsByClassName("state") ?? [],
      ) as HTMLElement[];

      // First, reset all regions to gray
      states.forEach((state) => {
        state.style.cssText =
          "fill: " +
          two_colors_model[0] +
          ";stroke: " +
          two_colors_borders[0] +
          ";stroke-opacity:1;";
      });

      // Add region names to states
      states.forEach((state) => {
        const region_name = get_region_by_to_map_connection(state.id);
        if (region_name) {
          if (!state.hasChildNodes()) {
            let new_child = document.createElementNS(
              "http://www.w3.org/2000/svg",
              "title",
            );
            let text = document.createTextNode(region_name);
            new_child.appendChild(text);
            state.appendChild(new_child);
          } else {
            (state.children[0] as HTMLElement).innerText = region_name;
          }
        }
      });

      // If vals is empty or undefined, just return - regions will stay gray
      if (!vals || vals.length === 0) {
        return;
      }

      const sd_type_v2 = new Map<
        number,
        { color: string; map_priority: number; name: string }
      >();
      sd_type.forEach((el) => {
        sd_type_v2.set(el.id, {
          color: el.color,
          map_priority: el.map_priority,
          name: el.name,
        });
      });

      let regions_array: MapValueType[] = [];
      vals.forEach((el) => {
        let has_region = false;
        regions_array.forEach((region1) => {
          if (el.name === region1.name) {
            has_region = true;

            const type1 = sd_type_v2.get(el.sd_type);
            const type2 = sd_type_v2.get(region1.sd_type);

            if (type1 && type2 && type1.map_priority > type2.map_priority) {
              region1.sd_type = el.sd_type;
            }
          }
        });
        if (!has_region) {
          regions_array.push({ ...el });
        }
      });

      regions_array.forEach((region1) => {
        const _state = root?.getElementById(region1.name) as HTMLElement | null;

        if (_state) {
          const typeData = sd_type_v2.get(region1.sd_type);
          if (typeData) {
            const color = typeData.color;
            _state.style.cssText =
              "fill: " + color + ";stroke:#EBEBEB;stroke-opacity:1  ";
          }
        }
      });
    },
    [safeRegionsList],
  );

  useEffect(() => {
    set_colors_from_sd_type(safeShutdownTypes, safeVals);
  }, [safeVals, safeShutdownTypes, set_colors_from_sd_type]);

  return (
    <div className="w-full block" data-testid="graph-map-wrapper">
      <SvgMap ref={mapObject as any} data-testid="svg-map" {...rest} />
    </div>
  );
};

export default GraphMap;
